import type { EventBusPayload } from "@/modules/event-bus";

import { useQueryClient } from "@tanstack/react-query";

import { ONE_MINUTE_IN_SECONDS } from "@/constants/Time";
import { isObservationCommentedNotificationPayload } from "@/entities/broadcast-notification";
import { useInvalidateNotifications } from "@/entities/notifications";
import { OBSERVATIONS_MODEL_KEYS } from "@/entities/observation";
import { useAppNotification } from "@/modules/app-notification";
import { CommentLink } from "@/modules/comment/comment-link";
import { OrganizationContextLabel } from "@/modules/organization/organization-context-label";
import { UserAvatar } from "@/ui/user-avatar";

export const useObservationActionCommented = () => {
  const { notify } = useAppNotification();
  const queryClient = useQueryClient();
  const { invalidateNotifications } = useInvalidateNotifications();

  const runAction = (payload: EventBusPayload) => {
    if (!isObservationCommentedNotificationPayload(payload)) return;

    notify.info({
      key: payload.id,
      title: payload.comment.author_name,
      description: payload.comment.content,
      descriptionRows: 3,
      icon: (
        <UserAvatar
          size="small"
          statusColor="info"
          user={{ name: payload.comment.author_name }}
          withTooltip
        />
      ),
      actions: (
        <>
          <CommentLink
            customContext={payload.context}
            intervention={payload.intervention}
            observation={payload.observation}
            onClick={() => notify.close(payload.id)}
          />
          <OrganizationContextLabel context={payload.context} isSmall />
        </>
      ),
      duration: ONE_MINUTE_IN_SECONDS,
    });

    queryClient.invalidateQueries({
      queryKey: [
        OBSERVATIONS_MODEL_KEYS.COMMENTS_LIST,
        "list",
        payload.observation.id,
      ],
    });

    invalidateNotifications("*");
  };

  return { runAction };
};
